<template>
    <div class="collector-page">
        <!-- 工具条：标题说明 + 操作 -->
        <div class="collector-toolbar">
            <div class="collector-toolbar__info">
                <span class="collector-toolbar__icon">
                    <el-icon>
                        <Box />
                    </el-icon>
                </span>
                <span class="collector-toolbar__meta">
                    <span class="collector-toolbar__title">数据收集器</span>
                    <span class="collector-toolbar__subtitle">
                        创建收集计划并启用后，使用计划 token 调用 /datacoll/create 上报 JSON 数据，用于后续查询与分析
                    </span>
                </span>
            </div>
            <div class="collector-toolbar__actions">
                <el-button class="toolbar-btn toolbar-btn--ghost" :loading="planLoading" @click="refresh">
                    <el-icon v-if="!planLoading" class="btn-icon">
                        <Refresh />
                    </el-icon>
                    刷新
                </el-button>
                <el-button class="toolbar-btn" type="primary" @click="openCreateCollected">
                    <el-icon class="btn-icon">
                        <Plus />
                    </el-icon>
                    创建计划
                </el-button>
            </div>
        </div>

        <!-- 分类切换：收集计划 / 收集数据（带数量，代替原标签页） -->
        <div class="collector-switch">
            <button type="button" class="collector-switch__item" :class="{ 'is-active': activeTab === 'plan' }"
                @click="switchTab('plan')">
                <el-icon class="collector-switch__icon">
                    <Tickets />
                </el-icon>
                <span>收集计划</span>
                <em class="collector-switch__count">{{ planList.length }}</em>
            </button>
            <button type="button" class="collector-switch__item" :class="{ 'is-active': activeTab === 'data' }"
                @click="switchTab('data')">
                <el-icon class="collector-switch__icon">
                    <DataLine />
                </el-icon>
                <span>收集数据</span>
                <em class="collector-switch__count">{{ dataList.length }}</em>
            </button>
        </div>

        <!-- 收集计划列表 -->
        <section class="collector-panel" v-if="activeTab === 'plan'" v-loading="planLoading">
            <el-empty v-if="planList.length === 0 && !planLoading" description="还没有收集计划，点击右上角「创建计划」开始" />
            <article class="plan-item" v-for="row in planList" :key="row.id" :class="{ 'is-off': !row.isEnable }">
                <span class="plan-item__icon">
                    <el-icon>
                        <Box />
                    </el-icon>
                </span>
                <div class="plan-item__main">
                    <div class="plan-item__head">
                        <span class="plan-item__name" :title="row.name">{{ row.name }}</span>
                        <span class="collector-chip" :class="row.isEnable ? 'collector-chip--on' : 'collector-chip--off'">
                            <el-icon class="collector-chip__icon">
                                <CircleCheck v-if="row.isEnable" />
                                <CircleClose v-else />
                            </el-icon>
                            {{ row.isEnable ? '收集中' : '已停用' }}
                        </span>
                    </div>
                    <div class="plan-item__desc" :title="row.description">{{ row.description || '暂无描述' }}</div>
                    <div class="plan-item__meta">
                        <span class="collector-chip collector-chip--count">
                            已收集 {{ Number(row.dataCount || 0).toLocaleString() }} 条
                        </span>
                        <span v-if="row.createTime">创建于 {{ formatTime(row.createTime) }}</span>
                    </div>
                </div>
                <div class="plan-item__token">
                    <el-tooltip placement="top" :content="'点击复制 token：' + row.token">
                        <button type="button" class="token-box" @click="copy(row.token, 'token 已复制')">
                            <el-icon class="token-box__icon">
                                <Key />
                            </el-icon>
                            <span class="token-box__text">{{ row.token }}</span>
                            <el-icon class="token-box__copy">
                                <CopyDocument />
                            </el-icon>
                        </button>
                    </el-tooltip>
                </div>
                <div class="plan-item__actions">
                    <el-tooltip placement="top" :content="row.isEnable ? '停用后接口将拒绝上报数据' : '启用后接口开始接收数据'">
                        <el-switch v-model="row.isEnable" class="plan-switch" :loading="!!row._enableLoading"
                            @change="enablePlan(row)" />
                    </el-tooltip>
                    <el-tooltip placement="top" :disabled="!hasData(row)"
                        content="该计划已有收集数据，需先清理数据后才能删除">
                        <span class="plan-item__danger">
                            <el-popconfirm width="230" title="删除操作不可撤回，确定删除该计划吗？" confirm-button-text="确定删除"
                                cancel-button-text="取消" @confirm="deletePlan(row)">
                                <template #reference>
                                    <el-button type="danger" plain :disabled="hasData(row)">
                                        <el-icon class="btn-icon">
                                            <Delete />
                                        </el-icon>
                                        删除
                                    </el-button>
                                </template>
                            </el-popconfirm>
                        </span>
                    </el-tooltip>
                </div>
            </article>
        </section>

        <!-- 收集数据列表 -->
        <template v-else>
            <div class="collector-filter">
                <el-input v-model="searchDataParams.planName" class="filter-input" placeholder="计划名称" clearable
                    @keydown.enter="searchData" />
                <el-input v-model="searchDataParams.dataSource" class="filter-input" placeholder="数据来源" clearable
                    @keydown.enter="searchData" />
                <el-input v-model="searchDataParams.ip" class="filter-input" placeholder="IP 地址" clearable
                    @keydown.enter="searchData" />
                <el-input v-model="searchDataParams.senderId" class="filter-input" placeholder="发送端 ID" clearable
                    @keydown.enter="searchData" />
                <el-input v-model="searchDataParams.senderName" class="filter-input" placeholder="发送端名称" clearable
                    @keydown.enter="searchData" />
                <el-button class="filter-btn" type="primary" :loading="dataLoading" @click="searchData">
                    <el-icon v-if="!dataLoading" class="btn-icon">
                        <Search />
                    </el-icon>
                    搜索
                </el-button>
                <el-button class="filter-btn" @click="resetSearch">重置</el-button>
                <span class="collector-filter__count">共 {{ dataList.length }} 条</span>
            </div>

            <section class="collector-panel" v-loading="dataLoading">
                <el-empty v-if="dataList.length === 0 && !dataLoading"
                    description="还没有收集到数据，启用计划后通过接口上报即可" />
                <article class="data-item" v-for="row in dataList" :key="row.id">
                    <span class="data-item__icon">
                        <el-icon>
                            <DataLine />
                        </el-icon>
                    </span>
                    <div class="data-item__main">
                        <div class="data-item__head">
                            <span class="data-item__plan" :title="row.planName">{{ row.planName || '—' }}</span>
                            <span class="collector-chip collector-chip--count" v-if="row.dataSource">
                                {{ row.dataSource }}
                            </span>
                            <span class="collector-chip" v-if="row.dataType">{{ row.dataType }}</span>
                            <span class="data-item__time" :title="row.createTime">{{ row.createTime }}</span>
                        </div>
                        <div class="data-item__meta">
                            <span class="data-item__meta-item">
                                <el-icon class="data-item__meta-icon">
                                    <User />
                                </el-icon>
                                {{ row.senderName || '未知发送端' }}
                                <template v-if="row.senderId">（{{ row.senderId }}）</template>
                            </span>
                            <span class="data-item__meta-item" v-if="row.ip">
                                <el-icon class="data-item__meta-icon">
                                    <Monitor />
                                </el-icon>
                                {{ row.ip }}
                            </span>
                            <span class="data-item__meta-item" v-if="row.mac">
                                <el-icon class="data-item__meta-icon">
                                    <Cpu />
                                </el-icon>
                                {{ row.mac }}
                            </span>
                        </div>
                        <div class="data-item__body" :class="{ 'is-collapsed': !isExpanded(row) }">
                            <pre class="data-item__json">{{ prettyJson(row.dataString) }}</pre>
                        </div>
                        <div class="data-item__tools">
                            <el-button link type="primary" size="small" @click="toggleExpand(row)">
                                {{ isExpanded(row) ? '收起' : '展开全部' }}
                            </el-button>
                            <el-button link size="small" @click="copy(row.dataString, 'JSON 已复制')">复制 JSON</el-button>
                        </div>
                    </div>
                </article>
            </section>
        </template>

        <!-- 创建 / 编辑计划 -->
        <el-dialog :title="dialogFormMode === 'create' ? '创建数据收集计划' : '编辑数据收集计划'" v-model="dialogFormVisible"
            class="plan-dialog" width="520px" :close-on-click-modal="false" @closed="resetForm">
            <el-form ref="planFormRef" :model="dataForm" :rules="formRules" label-width="86px">
                <el-form-item label="计划名称" prop="name">
                    <el-input v-model="dataForm.name" maxlength="50" show-word-limit autocomplete="off"
                        placeholder="例如：客户端性能监控"></el-input>
                </el-form-item>
                <el-form-item label="计划描述" prop="description">
                    <el-input type="textarea" :rows="3" maxlength="200" show-word-limit
                        placeholder="简要说明该计划收集的数据用途" v-model="dataForm.description"></el-input>
                </el-form-item>
                <el-form-item label="运行状态">
                    <el-switch v-model="dataForm.isEnable" class="plan-switch"></el-switch>
                    <span class="form-tip">开启后立即开始接收符合条件的数据</span>
                </el-form-item>
            </el-form>
            <template #footer>
                <span class="dialog-footer">
                    <el-button @click="dialogFormVisible = false">取 消</el-button>
                    <el-button type="primary" :loading="saving" @click="saveCollected">保 存</el-button>
                </span>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import { ElMessage } from 'element-plus';
import request from '../../../utils/request.js';
import { copy as copyText } from '../../../utils/clipboard.js';
import {
    Box, Plus, Refresh, Tickets, DataLine, Search, Key, CopyDocument,
    Delete, User, Monitor, Cpu, CircleCheck, CircleClose
} from '@element-plus/icons-vue';

export default {
    components: {
        Box, Plus, Refresh, Tickets, DataLine, Search, Key, CopyDocument,
        Delete, User, Monitor, Cpu, CircleCheck, CircleClose
    },
    data() {
        return {
            // plan：收集计划；data：收集数据
            activeTab: 'plan',
            planLoading: false,
            dataLoading: false,
            saving: false,
            dialogFormMode: 'create',
            dialogFormVisible: false,
            dataForm: {
                name: '',
                description: '',
                isEnable: false
            },
            formRules: {
                name: [{ required: true, message: '请输入计划名称', trigger: 'blur' }],
                description: [{ required: true, message: '请输入计划描述', trigger: 'blur' }]
            },
            // 已展开 JSON 详情的数据记录 id
            expandedIds: [],
            planList: [],
            dataList: [],
            searchDataParams: {
                createTime: '',
                planName: '',
                dataSource: '',
                ip: '',
                mac: '',
                senderId: '',
                senderName: '',
                dataString: ''
            }
        };
    },
    mounted() {
        this.loadPlan();
    },
    methods: {
        switchTab(tab) {
            this.activeTab = tab;
            // 进入「收集数据」时自动按当前条件查询一次，避免空白页
            if (tab === 'data') {
                this.searchData();
            }
        },
        refresh() {
            this.loadPlan();
            if (this.activeTab === 'data') {
                this.searchData();
            }
        },
        // 计划存在收集数据时后端不允许删除
        hasData(row) {
            return Number(row.dataCount || 0) > 0;
        },
        formatTime(value) {
            if (!value) return '';
            return String(value).replace('T', ' ').slice(0, 16);
        },
        prettyJson(text) {
            if (!text) return '{}';
            try {
                return JSON.stringify(JSON.parse(text), null, 2);
            } catch (e) {
                return String(text);
            }
        },
        isExpanded(row) {
            return this.expandedIds.includes(row.id);
        },
        toggleExpand(row) {
            const idx = this.expandedIds.indexOf(row.id);
            if (idx >= 0) {
                this.expandedIds.splice(idx, 1);
            } else {
                this.expandedIds.push(row.id);
            }
        },
        searchData() {
            this.searchDataParams.token = this.$store.state.user.token;
            this.dataLoading = true;
            return request({
                url: '/datacoll/list',
                method: 'post',
                data: this.searchDataParams
            }).then(res => {
                if (res.code == 0) {
                    this.dataList = res.data;
                }
            }).finally(() => {
                this.dataLoading = false;
            });
        },
        resetSearch() {
            this.searchDataParams = {
                createTime: '',
                planName: '',
                dataSource: '',
                ip: '',
                mac: '',
                senderId: '',
                senderName: '',
                dataString: ''
            };
            this.searchData();
        },
        openCreateCollected() {
            this.dialogFormMode = 'create';
            this.dataForm = { name: '', description: '', isEnable: false };
            this.dialogFormVisible = true;
        },
        resetForm() {
            this.dataForm = { name: '', description: '', isEnable: false };
            if (this.$refs.planFormRef) {
                this.$refs.planFormRef.clearValidate();
            }
        },
        loadPlan() {
            this.planLoading = true;
            return request({
                url: '/datacollplan/list',
                method: 'post',
                data: { token: this.$store.state.user.token }
            }).then(res => {
                if (res.code == 0) {
                    this.planList = res.meta.plans;
                }
            }).finally(() => {
                this.planLoading = false;
            });
        },
        saveCollected() {
            const form = this.$refs.planFormRef;
            if (!form) return;
            form.validate(valid => {
                if (valid) {
                    this.submitPlan();
                }
            });
        },
        submitPlan() {
            this.saving = true;
            let req;
            if (this.dialogFormMode == 'create') {
                req = request({
                    url: '/datacollplan/create',
                    method: 'post',
                    data: {
                        name: this.dataForm.name,
                        description: this.dataForm.description,
                        isEnable: this.dataForm.isEnable
                    }
                });
            } else {
                req = request({
                    url: '/datacollplan/edit',
                    method: 'post',
                    data: {
                        id: this.currentCollected.id,
                        name: this.dataForm.name,
                        description: this.dataForm.description,
                        isEnable: this.dataForm.isEnable
                    }
                });
            }
            return req.then(res => {
                if (res.code == 0) {
                    ElMessage({ message: '保存成功', type: 'success', duration: 2 * 1000 });
                    this.dialogFormVisible = false;
                    this.loadPlan();
                }
            }).finally(() => {
                this.saving = false;
            });
        },
        // 由开关直接切换启用/停用，v-model 已更新 isEnable，这里取当前值提交
        enablePlan(row) {
            const enable = row.isEnable;
            row._enableLoading = true;
            return request({
                url: '/datacollplan/enable',
                method: 'post',
                data: { token: this.$store.state.user.token, id: row.id, enable: enable }
            }).then(res => {
                if (res.code == 0) {
                    ElMessage({
                        message: enable ? '计划已启用，开始接收数据' : '计划已停用',
                        type: 'success',
                        duration: 2 * 1000
                    });
                    this.loadPlan();
                }
            }).catch(() => {
                // 请求失败时回滚开关，避免界面状态与后端不一致
                row.isEnable = !enable;
            }).finally(() => {
                row._enableLoading = false;
            });
        },
        deletePlan(row) {
            return request({
                url: '/datacollplan/delete',
                method: 'post',
                data: { token: this.$store.state.user.token, id: row.id }
            }).then(res => {
                if (res.code == 0) {
                    ElMessage({ message: '计划已删除', type: 'success', duration: 2 * 1000 });
                    this.loadPlan();
                }
            });
        },
        copy(s, tip) {
            if (!s) return;
            copyText(s);
            ElMessage({
                message: tip || `复制成功：${s}`,
                type: 'success',
                duration: 1 * 1000
            });
        }
    }
};
</script>

<style scoped>
.collector-page {
    /* 局部覆盖主色，与其它工作台页面保持同一套视觉语言 */
    --el-color-primary: #2f6fed;
    --el-color-primary-light-3: #6d9af2;
    --el-color-primary-light-5: #97b7f6;
    --el-color-primary-light-7: #c1d4fa;
    --el-color-primary-light-8: #d5e2fb;
    --el-color-primary-light-9: #eaf1fd;
    --el-color-primary-dark-2: #2659be;

    --collector-text: #1f2d3d;
    --collector-sub: #5b6a80;
    --collector-muted: #aab4c4;
    --collector-line: #eef1f6;
    --collector-border: #e6eaf0;

    color: var(--collector-text);
}

/* ---------- 工具条 ---------- */
.collector-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    row-gap: 10px;
    padding: 10px 16px;
    border: 1px solid #ebeef5;
    border-radius: 12px;
    background: linear-gradient(180deg, #ffffff 0%, #fafbfd 100%);
    box-shadow: 0 10px 28px -22px rgba(31, 45, 61, 0.55);
}

.collector-toolbar__info {
    display: flex;
    align-items: center;
    min-width: 0;
}

.collector-toolbar__icon {
    display: flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    margin-right: 10px;
    border-radius: 10px;
    font-size: 18px;
    color: #fff;
    background: linear-gradient(135deg, #6a9cff 0%, #2f6fed 100%);
    box-shadow: 0 8px 16px -10px rgba(47, 111, 237, 0.9);
}

.collector-toolbar__meta {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.collector-toolbar__title {
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    color: #1f2d3d;
}

.collector-toolbar__subtitle {
    font-size: 12px;
    line-height: 16px;
    color: #a8abb2;
}

.collector-toolbar__actions {
    display: flex;
    flex: none;
    flex-wrap: wrap;
    align-items: center;
    row-gap: 8px;
}

/* ---------- 工具条按钮 ---------- */
.toolbar-btn {
    height: 34px;
    padding: 0 16px;
    border-radius: 9px;
    font-weight: 500;
    transition: all 0.2s ease;
}

.toolbar-btn--ghost {
    color: #606266;
    background-color: #fff;
    border-color: #e4e7ed;
}

.toolbar-btn--ghost:hover,
.toolbar-btn--ghost:focus {
    color: var(--el-color-primary);
    background-color: var(--el-color-primary-light-9);
    border-color: var(--el-color-primary-light-5);
    transform: translateY(-1px);
    box-shadow: 0 10px 18px -12px rgba(47, 111, 237, 0.9);
}

.btn-icon {
    margin-right: 4px;
}

/* ---------- 分类切换 ---------- */
.collector-switch {
    display: flex;
    gap: 8px;
    margin-top: 14px;
}

.collector-switch__item {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    padding: 0 18px;
    border: 1px solid var(--collector-border);
    border-radius: 10px;
    background-color: #fff;
    color: var(--collector-sub);
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
}

.collector-switch__item:hover {
    color: var(--el-color-primary);
    border-color: var(--el-color-primary-light-5);
}

.collector-switch__item.is-active {
    color: #fff;
    border-color: transparent;
    background: linear-gradient(135deg, #2f6fed 0%, #2659be 100%);
    box-shadow: 0 10px 20px -12px rgba(47, 111, 237, 1);
}

.collector-switch__icon {
    font-size: 16px;
}

.collector-switch__count {
    padding: 0 8px;
    border-radius: 999px;
    background-color: #f2f5fa;
    color: var(--collector-sub);
    font-size: 12px;
    font-style: normal;
    line-height: 18px;
}

.collector-switch__item.is-active .collector-switch__count {
    background-color: rgba(255, 255, 255, 0.24);
    color: #fff;
}

/* ---------- 筛选 ---------- */
.collector-filter {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    margin-top: 12px;
}

.filter-input {
    flex: 0 1 170px;
    min-width: 140px;
}

.filter-input :deep(.el-input__wrapper) {
    border-radius: 9px;
}

.filter-btn {
    border-radius: 9px;
}

.collector-filter__count {
    font-size: 12px;
    color: var(--collector-muted);
}

/* ---------- 列表容器 ---------- */
.collector-panel {
    margin-top: 12px;
    padding: 6px;
    border: 1px solid var(--collector-border);
    border-radius: 14px;
    background-color: #fff;
    box-shadow: 0 1px 2px rgba(28, 47, 84, 0.05);
}

.collector-panel :deep(.el-empty) {
    padding: 40px 0;
}

/* ---------- 状态 / 信息标签 ---------- */
.collector-chip {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 1px 8px;
    border-radius: 999px;
    background-color: #f2f5fa;
    color: var(--collector-sub);
    font-size: 12px;
    line-height: 18px;
    white-space: nowrap;
}

.collector-chip__icon {
    font-size: 12px;
}

.collector-chip--on {
    background-color: #eaf7ef;
    color: #2ba471;
}

.collector-chip--off {
    background-color: #f2f3f5;
    color: #909399;
}

.collector-chip--count {
    background-color: var(--el-color-primary-light-9);
    color: var(--el-color-primary);
}

/* ---------- 收集计划 ---------- */
.plan-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px;
    border-radius: 10px;
    transition: background-color 0.2s;
}

.plan-item + .plan-item {
    border-top: 1px solid var(--collector-line);
}

.plan-item:hover {
    background-color: #f7faff;
}

.plan-item__icon {
    display: flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 11px;
    font-size: 19px;
    color: var(--el-color-primary);
    background-color: var(--el-color-primary-light-9);
}

.plan-item.is-off .plan-item__icon {
    color: #8a94a6;
    background-color: #f2f5fa;
}

.plan-item__main {
    flex: 1 1 auto;
    min-width: 0;
}

.plan-item__head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
}

.plan-item__name {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: 14px;
    font-weight: 600;
    color: var(--collector-text);
}

.plan-item__desc {
    margin-top: 4px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: 13px;
    color: var(--collector-sub);
}

.plan-item__meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    margin-top: 6px;
    font-size: 12px;
    color: var(--collector-muted);
}

.plan-item__token {
    flex: none;
}

/* token 展示：等宽字体 + 点击复制 */
.token-box {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: 230px;
    padding: 5px 10px;
    border: 1px dashed #cfd8e6;
    border-radius: 8px;
    background-color: #f8fafd;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 12px;
    color: var(--collector-sub);
    cursor: pointer;
    transition: all 0.2s ease;
}

.token-box:hover {
    border-color: var(--el-color-primary-light-5);
    background-color: var(--el-color-primary-light-9);
    color: var(--el-color-primary);
}

.token-box__icon {
    flex: none;
    font-size: 13px;
}

.token-box__text {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.token-box__copy {
    flex: none;
    opacity: 0;
    transition: opacity 0.2s;
}

.token-box:hover .token-box__copy {
    opacity: 1;
}

.plan-item__actions {
    display: flex;
    flex: none;
    align-items: center;
    gap: 12px;
}

.plan-item__danger {
    display: inline-flex;
}

.plan-switch {
    --el-switch-on-color: #2f6fed;
    --el-switch-off-color: #c8cdd8;
}

/* ---------- 收集数据 ---------- */
.data-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px;
    border-radius: 10px;
    transition: background-color 0.2s;
}

.data-item + .data-item {
    border-top: 1px solid var(--collector-line);
}

.data-item:hover {
    background-color: #f7faff;
}

.data-item__icon {
    display: flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 10px;
    font-size: 17px;
    color: var(--el-color-primary);
    background-color: var(--el-color-primary-light-9);
}

.data-item__main {
    flex: 1 1 auto;
    min-width: 0;
}

.data-item__head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
}

.data-item__plan {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: 14px;
    font-weight: 600;
    color: var(--collector-text);
}

.data-item__time {
    margin-left: auto;
    font-size: 12px;
    color: var(--collector-muted);
    white-space: nowrap;
}

.data-item__meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    margin-top: 6px;
    font-size: 12px;
    color: var(--collector-sub);
}

.data-item__meta-item {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.data-item__meta-icon {
    font-size: 13px;
    color: var(--collector-muted);
}

/* JSON 详情：默认折叠，可展开查看全部 */
.data-item__body {
    position: relative;
    margin-top: 8px;
    border: 1px solid var(--collector-line);
    border-radius: 8px;
    background-color: #f8fafd;
    overflow: hidden;
}

.data-item__body.is-collapsed {
    max-height: 96px;
}

.data-item__body.is-collapsed::after {
    content: '';
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    height: 36px;
    background: linear-gradient(180deg, rgba(248, 250, 253, 0) 0%, #f8fafd 100%);
    pointer-events: none;
}

.data-item__json {
    margin: 0;
    padding: 10px 12px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 12px;
    line-height: 18px;
    color: #33415c;
    white-space: pre-wrap;
    word-break: break-word;
}

.data-item__tools {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 2px;
}

/* ---------- 弹窗 ---------- */
.form-tip {
    margin-left: 10px;
    font-size: 12px;
    color: #a8abb2;
}

/* ---------- 小尺寸设备适配 ---------- */
@media (max-width: 768px) {
    .collector-switch {
        width: 100%;
    }

    .collector-switch__item {
        flex: 1 1 0;
        justify-content: center;
        padding: 0 10px;
    }

    .collector-filter {
        gap: 8px;
    }

    .filter-input {
        flex: 1 1 100%;
    }

    /* 窄屏下每条计划改为「图标 + 内容 + 操作」多行布局 */
    .plan-item {
        flex-wrap: wrap;
        row-gap: 10px;
        padding: 12px;
    }

    .plan-item__token {
        flex: 1 1 100%;
    }

    .token-box {
        max-width: 100%;
        width: 100%;
    }

    .plan-item__actions {
        flex: 1 1 100%;
        justify-content: flex-end;
    }

    .data-item {
        padding: 12px;
    }

    .data-item__time {
        margin-left: 0;
    }
}
</style>

<style>
/* 弹窗默认宽度在窄屏下收缩，避免超出屏幕（弹窗挂载在 body 上，需非 scoped 样式） */
@media (max-width: 600px) {
    .plan-dialog {
        width: 92vw !important;
    }
}
</style>
